// Goods receptions (HACCP part 2, docs/architecture.md §22.5): queries and calls. Kept beside queries.ts (same keys,
// under the 'haccp' group, so refreshScoped(qc, orgId, ['haccp']) refreshes them with the other HACCP data).

import type {
  Page, Reception, ReceptionCategory, ReceptionDetail, ReceptionInput, ReceptionProbe, Supplier,
} from '@cck/schema';
import { useQuery } from '@tanstack/react-query';
import { api } from './api.ts';
import { keys } from './queries.ts';

type List<T> = { data: T[] };
type Json = Record<string, unknown>;

export const useSuppliers = (o: string, archived = false) =>
  useQuery({ queryKey: keys.scoped(o, 'haccp', 'suppliers', archived),
    queryFn: () => api<List<Supplier>>(`/orgs/${o}/haccp/suppliers${archived ? '?archived=true' : ''}`) });
export const useReceptionCategories = (o: string, filter = '') =>
  useQuery({ queryKey: keys.scoped(o, 'haccp', 'reception-categories', filter),
    queryFn: () => api<List<ReceptionCategory>>(`/orgs/${o}/haccp/reception-categories${filter}`) });
/** The site's probes and their latest reading, refreshed every 30 seconds while the form is open. */
export const useReceptionProbes = (o: string, siteId: string, enabled = true) =>
  useQuery({ queryKey: keys.scoped(o, 'haccp', 'reception-probes', siteId), enabled: enabled && siteId !== '',
    refetchInterval: 30_000, queryFn: () => api<List<ReceptionProbe>>(`/orgs/${o}/haccp/reception-probes?site_id=${siteId}`) });
export const useReceptions = (o: string, filter: string) =>
  useQuery({ queryKey: keys.scoped(o, 'haccp', 'receptions', filter), refetchInterval: 60_000,
    queryFn: () => api<Page<Reception>>(`/orgs/${o}/haccp/receptions?limit=100${filter}`) });
export const useReception = (o: string, id: string) =>
  useQuery({ queryKey: keys.scoped(o, 'haccp', 'reception', id),
    queryFn: () => api<ReceptionDetail>(`/orgs/${o}/haccp/receptions/${id}`) });

export const receptionApi = (orgId: string) => ({
  createSupplier: (b: Json) => api<Supplier>(`/orgs/${orgId}/haccp/suppliers`, { method: 'POST', body: b }),
  updateSupplier: (id: string, b: Json) => api<Supplier>(`/orgs/${orgId}/haccp/suppliers/${id}`, { method: 'PATCH', body: b }),
  createCategory: (b: Json) => api<ReceptionCategory>(`/orgs/${orgId}/haccp/reception-categories`, { method: 'POST', body: b }),
  updateCategory: (id: string, b: Json) =>
    api<ReceptionCategory>(`/orgs/${orgId}/haccp/reception-categories/${id}`, { method: 'PATCH', body: b }),
  confirmCategory: (id: string) =>
    api<ReceptionCategory>(`/orgs/${orgId}/haccp/reception-categories/${id}/confirm`, { method: 'POST' }),
  // Offline-ready: the client's ID makes a retry safe (the same ID and content return the same reception).
  createReception: (b: ReceptionInput) => api<ReceptionDetail>(`/orgs/${orgId}/haccp/receptions`, { method: 'POST', body: b }),
  correctReception: (id: string, b: Json) =>
    api<ReceptionDetail>(`/orgs/${orgId}/haccp/receptions/${id}/corrections`, { method: 'POST', body: b }),
});
